import { isApolloError } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { CaptureContext, ScopeContext } from '@sentry/types';
import { CartPricingValidationErrors, SerializedCartValidationError } from '../types';
import analytics from './analytics';
import { isAxiosError } from './fi-api/apiUtils';
import i18next from '../../i18n';

export class UserFacingError extends Error {}
export class RecaptchaError extends Error {}

export class ThreeDSecureRequiredError extends UserFacingError {
  constructor(
    message: string,
    public readonly actionTokenId: string,
  ) {
    super(message);
  }
}

export class ApplePayAccountExistsError extends UserFacingError {
  constructor() {
    super(i18next.t('apple_pay_email_mismatch'));
  }
}

export class StripeCheckoutAccountExistsError extends UserFacingError {
  constructor(public readonly hasActiveMembership: boolean = false) {
    super(i18next.t('an_account_for_this_email_already_exists'));
  }
}

export class GenericApplePayError extends UserFacingError {
  constructor() {
    super(i18next.t('an_unknown_error_occurred_please_try_again_long'));
  }
}

export const APPLE_PAY_ERROR_CODE_SHIPPING = 'shippingContactInvalid';

// This is a custom error code for apple pay that we will be handling when the error is return from an API call
// to a Fi endpoint.
export const APPLE_PAY_ERROR_CODE_CUSTOM_API = 'fiApiError';
export const APPLE_PAY_ERROR_CODE_CUSTOM_ACCOUNT_EXISTS = 'accountExists';

export class UserExistsSignUpError extends UserFacingError {
  constructor() {
    super(i18next.t('an_account_for_this_email_already_exists'));
  }
}

export class BillingAccountCreationError extends UserFacingError {
  constructor() {
    super(i18next.t('we_were_unable_to_finish_creating_your_account'));
  }
}

export abstract class PromoCodeError<T> extends Error {
  constructor(
    message: string,
    public readonly type: T,
    public readonly underlying?: any,
  ) {
    super(message);
  }

  static notFound<T>(this: { new (message: string, type: 'not-found'): PromoCodeError<T> }, message: string) {
    return new this(message, 'not-found');
  }

  static ineligible<T>(this: { new (message: string, type: 'ineligible'): PromoCodeError<T> }, message: string) {
    return new this(message, 'ineligible');
  }

  static unknown<T>(
    this: { new (message: string, type: 'unknown', underlying: any): PromoCodeError<T> },
    underlying: any,
  ) {
    return new this(i18next.t('an_unknown_error_occurred'), 'unknown', underlying);
  }
}

type CouponErrorType = 'not-found' | 'unknown' | 'no-eligible-items' | 'ineligible';

export class CouponError extends PromoCodeError<CouponErrorType> {
  static noEligibleItems(message: string) {
    return new CouponError(message, 'no-eligible-items');
  }
}

type GiftCardErrorType = 'not-found' | 'unknown' | 'purchasing-gift-cards' | 'ineligible';

export class GiftCardError extends PromoCodeError<GiftCardErrorType> {
  static purchasingGiftCards() {
    return new GiftCardError(
      i18next.t('you_cannot_use_a_gift_card_to_purchase_another_gift_card'),
      'purchasing-gift-cards',
      null,
    );
  }
}

type ReferralCodeErrorType = 'not-found' | 'unknown' | 'code_suspended' | 'ineligible';

export class ReferralCodeError extends PromoCodeError<ReferralCodeErrorType> {
  static codeSuspended() {
    return new ReferralCodeError(i18next.t('referral_code_suspended'), 'code_suspended');
  }
}

export function throwCouponValidationError(error: SerializedCartValidationError): never {
  if (error.code === 'invalid_coupon') {
    throw CouponError.notFound(error.message);
  }

  if (error.code === 'coupon_no_eligible_items') {
    throw CouponError.noEligibleItems(error.message);
  }

  throw CouponError.ineligible(error.message);
}

export function throwGiftCardValidationError(error: SerializedCartValidationError): never {
  if (error.code === 'gift_card_purchasing_gift_card') {
    throw GiftCardError.purchasingGiftCards();
  }

  if (error.code === 'gift_card_invalid_redemption_code') {
    throw GiftCardError.notFound(error.message);
  }

  throw GiftCardError.ineligible(error.message);
}

export function throwReferralCodeValidationError(error: SerializedCartValidationError): never {
  if (error.code === 'invalid_referral_code') {
    throw ReferralCodeError.notFound(error.message);
  }

  if (error.code === 'referral_code_denylisted') {
    throw ReferralCodeError.codeSuspended();
  }

  throw ReferralCodeError.ineligible(error.message);
}

export function throwCartValidationError(validationErrors: CartPricingValidationErrors) {
  if (validationErrors.redeemedGiftCardCode) {
    throwGiftCardValidationError(validationErrors.redeemedGiftCardCode);
  }

  if (validationErrors.couponCode) {
    throwCouponValidationError(validationErrors.couponCode);
  }

  if (validationErrors.referralCode) {
    throwReferralCodeValidationError(validationErrors.referralCode);
  }
}

export function getCustomerMessageFromApolloError(e: any): string | undefined {
  if (isApolloError(e) && e.graphQLErrors.length > 0) {
    const extensions = e.graphQLErrors[0].extensions;
    if (extensions) {
      return extensions.customerMessage;
    }
  }
  return undefined;
}

export function getTokenFromApolloError(e: any): string | undefined {
  if (isApolloError(e) && e.graphQLErrors.length > 0) {
    const extensions = e.graphQLErrors[0].extensions;
    if (extensions) {
      return extensions.token;
    }
  }
  return undefined;
}

/**
 * From an error object, determines if the error is an Apollo (GraphQL API) or Axios (REST API) error and re-throws
 * a user-facing error message that contains either the customer-facing message from the API or a fallback message.
 */
export function throwUserFacingOrFallbackError(
  err: Error,
  fallbackError: Error = new UserFacingError(i18next.t('an_unknown_error_occurred_please_try_again_long')),
  sentryContext?: Omit<Partial<ScopeContext>, 'fingerprint'>,
): never {
  if (isApolloError(err)) {
    const customerMessage = getCustomerMessageFromApolloError(err);
    if (customerMessage) {
      throw new UserFacingError(customerMessage);
    }
  }

  // Error message for fingerprinting Sentry errors, if we make it that far.
  let errorMessage = err.message;

  const USER_FACING_ERROR_CODES = ['customer_error', 'ecommerce_error'];
  if (isAxiosError(err) && err.response?.status === 400 && err.response.data?.error?.message) {
    if (err.response.data.error.code && USER_FACING_ERROR_CODES.includes(err.response.data.error.code)) {
      throw new UserFacingError(err.response.data.error.message);
    }

    if (err.response.data.error.type === 'coupon_code') {
      throwCouponValidationError(err.response.data.error);
    }

    if (err.response.data.error.type === 'redeemed_gift_card_code') {
      throwGiftCardValidationError(err.response.data.error);
    }

    if (err.response.data.error.type === 'referral_code') {
      throwReferralCodeValidationError(err.response.data.error);
    }

    errorMessage = err.response.data.error.message;
  }

  logInternalError(err, {
    ...sentryContext,
    // Separate errors by the message in addition to Sentry's default fingerprint
    fingerprint: ['{{ default }}', errorMessage],
  });
  throw fallbackError;
}

export function logInternalError(err: string | Error, sentryCaptureContext?: CaptureContext) {
  console.error(err);
  const message = typeof err === 'string' ? err : err.message;
  analytics.track('Internal Error', { message });
  Sentry.captureException(err, sentryCaptureContext);
}

export const ErrorMessages = {
  get INPUT_ERROR() {
    return i18next.t('double_check_your_information_and_try_again');
  },
  get DEFAULT() {
    return i18next.t('reload_the_page_and_try_again');
  },
  get PURCHASE_FAILURE() {
    return i18next.t('we_were_unable_to_complete_your_purchase');
  },
};
export default ErrorMessages;
